import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Toaster } from '@react-starter/shared/components/shadcn/sonner';
import { TooltipProvider } from '@react-starter/shared/components/shadcn/tooltip';

import ErrorBoundary from '@/common/components/Errors/ErrorBoundary';
import ErrorFallback from '@/common/components/Errors/ErrorFallback';
import SettingsContextProvider from '@/common/providers/SettingsProvider';
import AxiosContextProvider from '@/common/providers/AxiosProvider';
import { router } from '@/common/components/Router/Router';
import Theme from '@/common/components/Theme/Theme';
import AuthContextProvider from '@/common/providers/AuthProvider';

/**
 * React Query `QueryClient` and configuration.
 * @see {@link https://tanstack.com/query/latest/docs/react/guides/important-defaults | Important Defaults}
 * @see {@link https://tanstack.com/query/latest/docs/react/reference/QueryClient | QueryClient}
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

/**
 * The application. The outermost component of the React application
 * hierarchy. Declares application-wide providers.
 */
const App = () => {
  return (
    <div id="app" className="bg-background text-foreground min-h-screen" data-testid="app">
      <ErrorBoundary fallback={<ErrorFallback />}>
        <QueryClientProvider client={queryClient}>
          <SettingsContextProvider>
            <Theme />
            <AuthContextProvider>
              <AxiosContextProvider>
                <TooltipProvider>
                  <RouterProvider router={router} />
                  <Toaster />
                </TooltipProvider>
              </AxiosContextProvider>
            </AuthContextProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </SettingsContextProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
};

export { App };
